<script setup lang="ts">
import { noLocationOption, useLocationResolver } from '@/components/locations/useLocationResolver'
import VsText from '@/components/vision/VsText.vue'
import PickerListVirtualized from '@/components/vision/next/picker-list/PickerListVirtualized.vue'
import VsButton from '@/components/vision/VsButton.vue'
import ChipFilter from '@/components/vision/next/ChipFilter.vue'
import AutoCompleteShell from '@/components/vision/next/inputs/AutoCompleteShell.vue'
import PickerListItem from '@/components/vision/next/picker-list/PickerListItem.vue'
import { useHeadlessLocationPicker } from '@/components/locations/HeadlessLocationPicker'
import { computed, onMounted, ref } from 'vue'
import { HeadlessAutoCompleteValueType } from '@/components/vision/next/inputs/useAutoComplete'
import type { MultiselectOption } from '@ironsight/types'
import { isSelectAllValue } from '@/components/jobs/job-search-page/useFilterChips'
import {
  useLocationSelectAll,
  type LocationPickerItem,
} from '@/components/jobs/job-search-page/useLocationSelectAll'

interface Props {
  value: Array<number | null> | MultiselectOption[]
  label?: string
  supportsSelectAll?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'input', value: Array<number | null> | MultiselectOption[]): void
}>()

const autocomplete = ref<InstanceType<typeof AutoCompleteShell>>()
const virtualizedList = ref<InstanceType<typeof PickerListVirtualized>>()

const getScrollPosition = () => {
  return virtualizedList?.value?.getScrollPosition()
}
const scrollToPosition = (pos: number) => {
  return virtualizedList?.value?.scrollToPosition(pos)
}
const onNavigate = () => {
  autocomplete.value?.input?.focus()
}

// In normal mode, derive internal selection from props (filtering out sentinel values).
const internalLocationSelection = ref<Array<number | null>>(
  Array.isArray(props.value)
    ? props.value.filter((v): v is number | null => typeof v === 'number' || v === null)
    : [],
)
const { locationValueResolver } = useLocationResolver()
const onLocationValueChanged = (items: HeadlessAutoCompleteValueType[]) => {
  // Ignore picker callbacks when in select-all mode — we manage state directly.
  if (isSelectAllValue(props.value)) return
  autocomplete.value?.input?.focus()
  internalLocationSelection.value = items.map((i) => i.id as number)
  emit('input', internalLocationSelection.value)
}

const {
  items,
  isFetching,
  locationType,
  locationTypes,
  selectLocationType,
  parentName,
  updateSearch,
  continueFetching,
  navigateIntoItem,
  navigateBack,
  selectItem,
  noResultsMessage,
  clear: clearLocationSearch,
  rawSearchText: locationRawSearchText,
  parentId,
  ancestorIds,
} = useHeadlessLocationPicker({
  getScrollPosition,
  scrollToPosition,
  onNavigate,
  onValueChanged: onLocationValueChanged,
  multiple: true,
  value: internalLocationSelection,
  valueResolver: locationValueResolver,
})
const showNoLocationOption = computed(() => {
  return !locationType.value?.id && !parentName.value && !locationRawSearchText.value
})
const handleLocationTypeUpdate = (val) => {
  selectLocationType(val)
}

onMounted(() => {
  autocomplete.value?.focus?.()
})

function handleClearLocationSearch() {
  clearLocationSearch()
}

const {
  isSelectAllMode,
  isItemSelected,
  toggleItem: toggleSelectAllItem,
  selectAllFiltered,
  deselectAllFiltered,
  areAllFilteredSelected,
  filteredOptions,
} = useLocationSelectAll({
  value: () => props.value,
  searchText: locationRawSearchText,
  selectedLocationIds: internalLocationSelection,
  items,
  showNoLocationOption,
  locationType,
  parentName,
  parentId,
  ancestorIds,
  emitInput: (value) => emit('input', value),
})

function handleSelectItem(item: HeadlessAutoCompleteValueType) {
  if (isSelectAllMode.value) {
    toggleSelectAllItem(item as LocationPickerItem)
    return
  }

  selectItem(item)
}

defineExpose(
  props.supportsSelectAll
    ? {
        supportsSelectAll: true,
        filteredOptions,
        selectAllFiltered,
        deselectAllFiltered,
        areAllFilteredSelected,
      }
    : {},
)
</script>

<template>
  <div class="location-filter">
    <div v-if="parentName" class="header">
      <vs-button
        small
        type="secondary"
        icon="mdi-chevron-left"
        :label="parentName"
        @click.stop="navigateBack"
      />
    </div>

    <AutoCompleteShell
      ref="autocomplete"
      :label="props.label || 'Search'"
      small-chips
      keep-value-on-clicking-outside
      @update:search="updateSearch"
      @clear="handleClearLocationSearch"
    >
      <template #append>
        <v-icon>search</v-icon>
      </template>
    </AutoCompleteShell>

    <chip-filter
      class="pt-2"
      :items="locationTypes"
      :value="locationType"
      @update="handleLocationTypeUpdate"
    />

    <div v-if="items.length === 0 && isFetching">
      <vs-text center>loading...</vs-text>
    </div>

    <picker-list-virtualized ref="virtualizedList" :items="items" @near-bottom="continueFetching">
      <template #before>
        <picker-list-item
          v-if="showNoLocationOption"
          checkbox
          :item="noLocationOption"
          :is-selected="isItemSelected(noLocationOption)"
          @select="handleSelectItem"
        />
      </template>
      <template #item="{ item }">
        <picker-list-item
          checkbox
          :item="item"
          :is-selected="isItemSelected(item)"
          @expand="navigateIntoItem"
          @select="handleSelectItem"
        />
      </template>
      <template #after>
        <v-container v-if="isFetching" class="my-2">
          <v-row justify="center">
            <vs-text center>loading...</vs-text>
          </v-row>
        </v-container>
      </template>
    </picker-list-virtualized>
    <div v-if="noResultsMessage" class="no-data">
      <v-container class="my-2">
        <v-row justify="center">
          <vs-text center>{{ noResultsMessage }}</vs-text>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<style scoped>
.location-filter {
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
  max-width: 100%;
}

:deep(.field-wrapper) {
  max-height: 200px;
  overflow-y: scroll;
}
</style>
